import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ChatSession } from '../types';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  sessions: ChatSession[];
  currentSession: string;
  onNewSession: () => void;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  width: number;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  sessions,
  currentSession,
  onNewSession,
  onSelectSession,
  onDeleteSession,
  width,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleNewChat = () => {
    onNewSession();
    if (isMobile) {
      onClose();
    }
  };

  const handleSelectSession = (sessionId: string) => {
    onSelectSession(sessionId);
    if (isMobile) {
      onClose();
    }
  };

  const handleDeleteSession = (sessionId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent selecting the session
    if (window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      onDeleteSession(sessionId);
    }
  };

  const drawerContent = (
    <Box 
      sx={{ 
        width: width, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: '#FAFAFA', // Light grey background
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Chat History
          </Typography>
        </Box>
        {isMobile && (
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'grey.100' },
              zIndex: 1000,
              p: 1.5,
            }}
            aria-label="Close sidebar"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* New Chat Button */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewChat}
          sx={{
            py: 1.5,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            boxShadow: '0 2px 8px rgba(0, 172, 221, 0.2)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 172, 221, 0.3)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Sessions List */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
        {sessions.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <ChatIcon sx={{ fontSize: 56, mb: 2, opacity: 0.3, color: 'grey.400' }} />
            <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
              No chat history yet.{'\n'}Start a new conversation!
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 1 }}>
            {sessions.map((session) => (
              <ListItem key={session.id} disablePadding sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 3,
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: session.id === currentSession ? 'primary.main' : 'grey.200',
                    boxShadow: session.id === currentSession 
                      ? '0 2px 8px rgba(0, 172, 221, 0.15)' 
                      : '0 1px 3px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      backgroundColor: session.id === currentSession ? 'primary.50' : 'grey.50',
                      borderColor: 'primary.main',
                      boxShadow: '0 2px 8px rgba(0, 172, 221, 0.15)',
                      transform: 'translateY(-1px)',
                      '& .delete-button': {
                        opacity: 1,
                      },
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {/* Main clickable area */}
                  <Box
                    onClick={() => handleSelectSession(session.id)}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.5,
                      px: 2,
                      cursor: 'pointer',
                      minWidth: 0, // Allow flex item to shrink below content size
                    }}
                  >
                    <ChatIcon
                      sx={{
                        fontSize: 20,
                        color: session.id === currentSession ? 'primary.main' : 'grey.500',
                        mr: 1.5,
                        flexShrink: 0, // Don't shrink the icon
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: session.id === currentSession ? 600 : 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: session.id === currentSession ? 'primary.main' : 'text.primary',
                        fontSize: '0.9rem',
                        flex: 1,
                        minWidth: 0, // Allow text to shrink and show ellipsis
                      }}
                    >
                      {session.title.length > 25 ? `${session.title.substring(0, 25)}...` : session.title}
                    </Typography>
                  </Box>
                  
                  {/* Delete button */}
                  <IconButton
                    className="delete-button"
                    size="small"
                    onClick={(event) => handleDeleteSession(session.id, event)}
                    sx={{
                      opacity: 0, // Show only on hover
                      transition: 'opacity 0.2s ease-in-out',
                      mr: 0.5,
                      p: 0.5, // Smaller padding
                      flexShrink: 0, // Don't shrink the delete button
                      '&:hover': {
                        backgroundColor: 'error.50',
                        color: 'error.main',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          borderTop: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'white',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.disabled',
            textAlign: 'center',
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 400,
          }}
        >
          © 2025 Burn Boot Camp
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            zIndex: (theme) => theme.zIndex.appBar - 1,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: width,
              pt: 8, // Padding top for header, not margin
              height: '100vh',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="persistent"
          open={open}
          sx={{
            width: open ? width : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: width,
              boxSizing: 'border-box',
              mt: 8, // Account for header height
              height: `calc(100vh - 64px)`,
              borderRight: '1px solid #E0E0E0',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar; 